import { useEffect, useState } from 'react';
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Inbox, FileSpreadsheet } from 'lucide-react';
import { api, API_BASE, getToken } from '../api';
import { SEC_EVENT_KO, SEC_EVENT_OPTIONS, parseUserAgent, utcToKst } from '../labels';
import { SkeletonTable } from '../components/Skeleton';

type Evt = {
  id: number;
  ts: string;
  event_type: string;
  actor_user_id: number | null;
  actor_username: string | null;
  target_user_id: number | null;
  target_label: string | null;
  ip: string | null;
  user_agent: string | null;
  detail: string | null;
  success: number;
};

const PAGE_SIZES = [50, 100, 200, 500];
const RESULT_OPTIONS = [
  { v: '',        k: '전체' },
  { v: 'success', k: '성공만' },
  { v: 'fail',    k: '실패만' },
] as const;

// 사용자 화면 표기는 KST — 필터 input에 넣을 문자열도 KST(YYYY-MM-DD HH:MM:SS).
// 서버로 보낼 때 kstStrToUtc()로 다시 UTC 문자열로 환산.
function kstNow(): string {
  const kst = new Date(Date.now() + 9 * 3600_000);
  return kst.toISOString().replace('T', ' ').slice(0, 19);
}
function kstShiftHours(h: number): string {
  const kst = new Date(Date.now() + h * 3600_000 + 9 * 3600_000);
  return kst.toISOString().replace('T', ' ').slice(0, 19);
}
// KST 문자열 → UTC 문자열 (서버 저장 형식). 잘못된 입력은 그대로 반환.
function kstStrToUtc(s: string): string {
  if (!s) return s;
  const iso = /[TZ]|[+-]\d{2}:\d{2}$/.test(s) ? s : s.replace(' ', 'T') + '+09:00';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return s;
  return d.toISOString().replace('T', ' ').slice(0, 19);
}

// 상세(JSON) 문자열을 사람이 읽기 쉬운 요약 문구로 변환.
// 실패 사유 관련 필드(reason/from/to)가 있으면 우선 노출.
function summarizeDetail(evt: Evt): string {
  const raw = evt.detail;
  if (!raw) return '';
  try {
    const j = JSON.parse(raw);
    const reasonMap: Record<string, string> = {
      unknown_user_or_inactive: '알 수 없는 계정 또는 비활성 계정',
      bad_password: '비밀번호 불일치',
      duplicate_username: '이미 등록된 아이디',
    };
    const parts: string[] = [];
    if (j.reason) parts.push(reasonMap[j.reason] ?? String(j.reason));
    if (j.from !== undefined || j.to !== undefined) {
      parts.push(`${j.from ?? '-'} → ${j.to ?? '-'}`);
    }
    if (j.by_admin) parts.push('관리자 처리');
    if (j.mode) parts.push(String(j.mode));
    if (j.role) parts.push(`권한: ${j.role}`);
    if (j.name && typeof j.name === 'object') {
      parts.push(`이름: ${j.name.from ?? '-'} → ${j.name.to ?? '-'}`);
    } else if (j.name != null) {
      parts.push(`이름: ${j.name}`);
    }
    if (j.position && typeof j.position === 'object') {
      parts.push(`직급: ${j.position.from ?? '-'} → ${j.position.to ?? '-'}`);
    }
    if (j.max) parts.push(`상한 ${j.max}회`);
    if (j.window_min) parts.push(`창 ${j.window_min}분`);
    return parts.filter(Boolean).join(' · ') || raw;
  } catch {
    return raw;
  }
}

const HIGHLIGHT_EVENTS = new Set(['rate_limited', 'login_fail', 'captcha_fail']);

export default function SecurityEvents() {
  // URL 쿼리 ?actor=xxx 로 진입 시 자동 필터. 접속중인 계정 화면의 [활동 상세] 링크에서 사용.
  const initialActor = (typeof window !== 'undefined')
    ? (new URLSearchParams(window.location.search).get('actor') ?? '')
    : '';
  const [data, setData] = useState<Evt[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState('');
  const [actor, setActor] = useState(initialActor);
  const [target, setTarget] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [result, setResult] = useState<'' | 'success' | 'fail'>('');
  const [ip, setIp] = useState('');
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (type) qs.set('type', type);
      if (actor) qs.set('actor', actor);
      if (target) qs.set('target', target);
      if (from) qs.set('from', kstStrToUtc(from));
      if (to) qs.set('to', kstStrToUtc(to));
      if (result) qs.set('result', result);
      if (ip) qs.set('ip', ip);
      qs.set('limit', String(limit));
      qs.set('offset', String(offset));
      const r = await api<{ data: Evt[]; total: number }>('GET', `/security/events?${qs.toString()}`);
      setData(r.data ?? []);
      setTotal(r.total ?? 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type, result, limit, offset]);

  const applyPreset = (h: number) => {
    setFrom(kstShiftHours(-h));
    setTo(kstNow());
    setOffset(0);
    setTimeout(load, 0);
  };

  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div>
      <h2>보안 로그</h2>
      <div className="card">
        <div className="toolbar" style={{ flexWrap: 'wrap', gap: 12 }}>
          <div>
            <label>이벤트 종류</label>
            <select value={type} onChange={e => { setType(e.target.value); setOffset(0); }}>
              <option value="">전체</option>
              {SEC_EVENT_OPTIONS.map(t => <option key={t} value={t}>{SEC_EVENT_KO[t] ?? t}</option>)}
            </select>
          </div>
          <div>
            <label>결과</label>
            <select value={result} onChange={e => { setResult(e.target.value as any); setOffset(0); }}>
              {RESULT_OPTIONS.map(o => <option key={o.v || 'all'} value={o.v}>{o.k}</option>)}
            </select>
          </div>
          <div>
            <label>행위자(아이디)</label>
            <input value={actor} onChange={e => setActor(e.target.value)} placeholder="username 일부" />
          </div>
          <div>
            <label>대상</label>
            <input value={target} onChange={e => setTarget(e.target.value)} placeholder="대상 username 일부" />
          </div>
          <div>
            <label>IP</label>
            <input value={ip} onChange={e => setIp(e.target.value)} placeholder="예: 61.79.57.180" style={{ width: 140 }} />
          </div>
          <div>
            <label>시작일시 (KST)</label>
            <input value={from} onChange={e => setFrom(e.target.value)} placeholder="YYYY-MM-DD HH:MM:SS" />
          </div>
          <div>
            <label>종료일시 (KST)</label>
            <input value={to} onChange={e => setTo(e.target.value)} placeholder="YYYY-MM-DD HH:MM:SS" />
          </div>
          <button className="btn" onClick={() => { setOffset(0); load(); }}>조회</button>
          <button className="btn secondary" onClick={() => {
            setType(''); setActor(''); setTarget(''); setFrom(''); setTo(''); setResult(''); setIp(''); setOffset(0);
          }}>초기화</button>
          <button className="btn secondary"
                  onClick={async () => {
                    const qs = new URLSearchParams();
                    if (type) qs.set('type', type);
                    if (actor) qs.set('actor', actor);
                    if (target) qs.set('target', target);
                    if (from) qs.set('from', kstStrToUtc(from));
                    if (to) qs.set('to', kstStrToUtc(to));
                    if (result) qs.set('result', result);
                    if (ip) qs.set('ip', ip);
                    const tok = getToken();
                    const res = await fetch(`${API_BASE}/security/events.csv?${qs.toString()}`, {
                      headers: tok ? { Authorization: `Bearer ${tok}` } : undefined,
                    });
                    if (!res.ok) { alert(`다운로드 실패: HTTP ${res.status}`); return; }
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    const tag = from && to ? `${from.slice(0, 10)}_${to.slice(0, 10)}` : new Date().toISOString().slice(0, 10);
                    a.download = `서포트리_보안로그_${tag}.csv`;
                    document.body.appendChild(a); a.click(); a.remove();
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                  }}
                  title="현재 필터 조건의 보안 로그를 CSV로 다운로드 (최대 50,000행, UTF-8 BOM)">
            <FileSpreadsheet size={12} /> CSV 다운로드
          </button>
        </div>
        <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>빠른 기간</span>
          {[
            { l: '오늘', h: 24 },
            { l: '최근 24시간', h: 24 },
            { l: '최근 7일', h: 24 * 7 },
            { l: '최근 30일', h: 24 * 30 },
          ].map(p => (
            <button key={p.l} onClick={() => applyPreset(p.h)} className="filter-ghost" style={{ fontSize: 12, padding: '4px 10px' }}>{p.l}</button>
          ))}
        </div>

        {loading ? <SkeletonTable rows={10} cols={8} showHeader /> : data.length === 0 ? (
          <div className="empty-state">
            <Inbox className="empty-icon" size={36} strokeWidth={1.6} />
            <h4>조회 결과 없음</h4>
            <p>필터를 조정해 보세요.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th className="num nowrap">#</th>
                <th className="nowrap">시각 (KST)</th>
                <th className="nowrap">종류</th>
                <th className="nowrap">결과</th>
                <th>행위자</th>
                <th>대상</th>
                <th className="nowrap">IP · 접속환경</th>
                <th>상세</th>
              </tr></thead>
              <tbody>
                {data.map(e => {
                  const highlight = HIGHLIGHT_EVENTS.has(e.event_type) && !e.success;
                  const summary = summarizeDetail(e);
                  const ua = parseUserAgent(e.user_agent);
                  const isOpen = expanded === e.id;
                  return (
                    <tr key={e.id} style={highlight ? { background: '#fee2e2' } : undefined}>
                      <td className="num nowrap" style={{ color: 'var(--text-muted)', fontFamily: 'monospace' }}>{e.id}</td>
                      <td className="nowrap" style={{ fontFamily: 'monospace' }} title={`서버 저장(UTC): ${e.ts}`}>{utcToKst(e.ts)}</td>
                      <td className="nowrap">
                        <span className="chip" style={highlight ? { background: '#b91c1c', color: '#fff' } : undefined}>
                          {SEC_EVENT_KO[e.event_type] ?? e.event_type}
                        </span>
                      </td>
                      <td className="nowrap">
                        {e.success
                          ? <span style={{ color: 'var(--leaf-700)', fontWeight: 600 }}>성공</span>
                          : <span style={{ color: '#b91c1c', fontWeight: 600 }}>실패</span>}
                      </td>
                      <td>
                        {e.actor_username
                          ? <span style={{ fontFamily: 'monospace' }}>{e.actor_username}{e.actor_user_id ? <span className="muted"> #{e.actor_user_id}</span> : null}</span>
                          : <span className="muted">-</span>}
                      </td>
                      <td>
                        {e.target_label
                          ? <span style={{ fontFamily: 'monospace' }}>{e.target_label}{e.target_user_id ? <span className="muted"> #{e.target_user_id}</span> : null}</span>
                          : <span className="muted">-</span>}
                      </td>
                      <td className="nowrap">
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)', fontSize: 12 }}>{e.ip ?? '-'}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{ua.os} · {ua.browser}</span>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 12, color: 'var(--text)' }}>{summary || '-'}</span>
                          {e.detail && (
                            <button
                              className="filter-ghost"
                              style={{ padding: '1px 6px', fontSize: 10 }}
                              onClick={() => setExpanded(v => v === e.id ? null : e.id)}
                            >{isOpen ? '숨기기' : '원본'}</button>
                          )}
                        </div>
                        {isOpen && e.detail && (
                          <pre style={{
                            marginTop: 4, padding: 6, background: '#f8fafc',
                            border: '1px solid var(--border)', borderRadius: 4,
                            fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all', whiteSpace: 'pre-wrap',
                          }}>{e.detail}</pre>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="toolbar" style={{ justifyContent: 'space-between', marginTop: 12 }}>
          <div className="muted">총 <b style={{ color: 'var(--leaf-800)' }}>{total.toLocaleString('ko-KR')}</b>건 · {page}/{totalPages} 페이지</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setOffset(0); }}>
              {PAGE_SIZES.map(n => <option key={n} value={n}>{n}/페이지</option>)}
            </select>
            <button className="btn secondary sm" onClick={() => setOffset(0)} disabled={offset === 0}><ChevronsLeft size={14} /></button>
            <button className="btn secondary sm" onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0}><ChevronLeft size={14} /></button>
            <button className="btn secondary sm" onClick={() => setOffset(offset + limit)} disabled={page >= totalPages}><ChevronRight size={14} /></button>
            <button className="btn secondary sm" onClick={() => setOffset((totalPages - 1) * limit)} disabled={page >= totalPages}><ChevronsRight size={14} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
